import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Content } from 'models/Content';
import { AppServices } from 'services/AppServices';
import Locale from 'localization/Localization';
import { ModalView } from 'components/common/ModalView';
import { ResultMessage, ResultStyle, ResultMessageView } from '../common/ResultMessageView';
import { ContentSets } from './ContentSets';
import { Layout } from 'components/common/Layout';
import { Button } from 'react-bootstrap';

interface ContentEditPageState {
	id: string;
	title: string;
	description: string;
	eula?: string;
	ptId: string;

	imageData?: string;
	imageUrl?: string;

	showEula: boolean;
	showUploadDialog: boolean;
	createNew: boolean;
	resultMessage?: ResultMessage;
	busy: boolean;
}

interface LocationState {
	content: any;
}

export class ContentEditPage extends React.Component<RouteComponentProps<{}>, ContentEditPageState>
{
	private static readonly maxFileSizeMB = 2;
	private static readonly maxFileSizeBytes = ContentEditPage.maxFileSizeMB * 1024 * 1024;
	private static readonly supportedFileExtensions = ['jpg', 'jpeg', 'png'];

	constructor(props: RouteComponentProps<{}>) {
		super(props);

		let data = new Content();
		let create = true;
		let url: string | undefined;

		const locationState = this.props.location.state as LocationState | undefined;

		if (locationState !== undefined && locationState.content !== undefined) {
			data = (this.props.location.state as LocationState).content;
			create = false;
			url = data.image.getUrl();
		}

		this.state = {
			id: data.contentId,
			title: data.title,
			description: data.description,
			eula: data.eula,
			imageUrl: url,
			ptId: data.physioToolsId,
			showEula: true,
			showUploadDialog: false,
			createNew: create,
			busy: false
		};

		this.onBackClicked = this.onBackClicked.bind(this);
		this.toggleEula = this.toggleEula.bind(this);
		this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.onEditTitle = this.onEditTitle.bind(this);
		this.onEditEula = this.onEditEula.bind(this);
		this.onEditDescription = this.onEditDescription.bind(this);
		this.onSelectImage = this.onSelectImage.bind(this);
		this.onDeleteImage = this.onDeleteImage.bind(this);
	}

	private toggleEula() {
		this.setState({
			showEula: !this.state.showEula
		});
	}

	private onEditTitle(event: React.ChangeEvent<HTMLInputElement>) {
		this.setState({ title: event.target.value });
	}

	private onEditEula(event: React.ChangeEvent<HTMLTextAreaElement>) {
		this.setState({ eula: event.target.value });
	}

	private onEditDescription(event: React.ChangeEvent<HTMLTextAreaElement>) {
		this.setState({ description: event.target.value });
	}

	private onBackClicked() {
		this.props.history.push('/content', { id: this.state.id });
	}

	private toggleUploadDialog() {
		this.setState({ showUploadDialog: !this.state.showUploadDialog });
	}

	private onSelectImage(event: React.ChangeEvent<HTMLInputElement>) {
		const files = event.target.files as FileList;
		const file = files[0];

		// Check file extension
		const extension = event.target.value.substring(
			event.target.value.lastIndexOf('.') + 1).toLocaleLowerCase();

		if (ContentEditPage.supportedFileExtensions.indexOf(extension) < 0) {
			this.setState({
				imageData: undefined,
				resultMessage: new ResultMessage(Locale.get('contentEdit_invalidExtension'), ResultStyle.Error),
				showUploadDialog: false
			});

			return;
		}

		// Check file size
		if (file.size > ContentEditPage.maxFileSizeBytes) {
			this.setState({
				imageData: undefined,
				resultMessage: new ResultMessage(Locale.get('contentEdit_fileSizeExceeded'), ResultStyle.Error),
				showUploadDialog: false
			});
			return;
		}

		// Validate that the image file is valid
		var reader = new FileReader();
		reader.onload = (e: ProgressEvent) => {
			let reader = e.target as FileReader;

			if (typeof reader.result === 'string') {
				this.setState({
					imageData: reader.result,
					imageUrl: reader.result
				});
			}
		};

		this.setState({
			resultMessage: undefined,
			showUploadDialog: false
		});

		reader.readAsDataURL(file);
	}

	private onDeleteImage() {
		this.setState({
			imageData: '',
			imageUrl: undefined
		});
	}

	private onSubmit() {
		const data = new Content();
		data.contentId = this.state.id;
		data.title = this.state.title;
		data.description = this.state.description;
		data.eula = this.state.eula;

		if (this.state.imageData !== undefined) {
			const dataSegments = this.state.imageData.split(',');
			if (dataSegments.length === 2) {
				data.imageData = dataSegments[1];
			}
			else {
				data.imageData = '';
			}
		}

		console.log(data.imageData);

		this.setState({ busy: true });

		const handleResult = (status: number) => {
			if (status === 204) // No content == success!
			{
				this.setState({
					busy: false,
					resultMessage: new ResultMessage(Locale.get("contentEdit_resultSuccess"), ResultStyle.Success)
				});

				this.props.history.push('/content');
			}
			else {
				console.log(status);
				this.setState({
					busy: false,
					resultMessage: new ResultMessage(Locale.get("contentEdit_resultFailed") + " (" + status + ")", ResultStyle.Error)
				});
			}
		};

		if (this.state.createNew) {
			AppServices.contentService.createContent(data).then(handleResult);
		}
		else {
			AppServices.contentService.editContent(data).then(handleResult);
		}
	}

	private static getUploadDialogMessage() {
		const fileSizeMb = ContentEditPage.maxFileSizeMB.toString();
		const extensions = ContentEditPage.supportedFileExtensions.join(', ');
		return Locale.format('contentEdit_fileDialogMessage', fileSizeMb, extensions);
	}

	public componentDidMount() {

	}

	public render() {
		const content = <div className='program-content'>
			<div>
				<Button variant='primary' onClick={this.onBackClicked}>{Locale.get('button_back')}</Button>
				<h1>{this.state.createNew ? Locale.get('contentEdit_titleCreate') : Locale.get('contentEdit_titleEdit')}</h1>
			</div>

			<div>
				<h2>{Locale.get('contentEdit_labelTitle')}</h2>
				<input type='text' defaultValue={this.state.title} onChange={this.onEditTitle} />
			</div>

			<div className='content-editor'>
				<div className='content-panel-image'>
					<h2>{Locale.get('contentEdit_labelImage')}</h2>
					{
						this.state.imageUrl === undefined
							? <React.Fragment>
								<div className='btn btn-link' onClick={this.toggleUploadDialog}>{Locale.get('contentEdit_buttonSelect')}</div>
							</React.Fragment>
							: <React.Fragment>
								<img className='content-image' src={this.state.imageUrl} alt='Content' />
								<div className='btn btn-link' onClick={this.onDeleteImage}>{Locale.get('contentEdit_buttonDeleteImage')}</div>
							</React.Fragment>
					}
				</div>
				<div className='content-panel-desc'>
					<h2>{Locale.get('contentEdit_labelDesc')}</h2>
					<textarea className='content-desc' cols={40} rows={5}
						defaultValue={this.state.description}
						onChange={this.onEditDescription}
					/>
					<ResultMessageView message={this.state.resultMessage} />
					<Button variant='outline-primary' className='btn-square' onClick={this.onSubmit}>{Locale.get('contentEdit_buttonSave')}</Button>
				</div>

			</div>

			<div className='content-eula'>
				<div className='content-eula-header'>
					<h2>{Locale.get('contentEdit_labelEula')}</h2>
					<div className='btn btn-link' onClick={this.toggleEula}>
						{
							this.state.showEula
								? Locale.get('contentEdit_buttonHide')
								: Locale.get('contentEdit_buttonShow')
						}
					</div>
				</div>
				{
					this.state.showEula &&
					<textarea
						defaultValue={this.state.eula ? this.state.eula : ''}
						onChange={this.onEditEula}
					/>
				}
			</div>

			<ContentSets contentId={this.state.id} ptId={this.state.ptId} />
		</div>;

		const fileSelect = (
			<ModalView show={this.state.showUploadDialog} onClose={this.toggleUploadDialog} >
				<div className='pad1em'>
					<input type='file' className='btn' onChange={this.onSelectImage} accept='image/*' />
					<p>{ContentEditPage.getUploadDialogMessage()}</p>
				</div>
			</ModalView>
		);

		return <Layout>{content}{fileSelect}</Layout>
	}
}